import './styles.scss';

import { Dayts, createNotification, formatDollars, uuid } from '@/utils';
import {
  InvoiceChargeTypeEnum,
  InvoiceCustomCharge,
  InvoiceCustomChargeInput,
  InvoiceDocumentGenDocumentStatusEnum,
  InvoiceRateSubtotal,
  InvoiceResponse,
  InvoiceTrip,
  InvoiceTripTypeEnum,
  RunPreviewInvoiceResponse,
} from '@/models/gen/graphql';
import InvoicePreviewFilters, { InvoicePreviewFiltersState, initInvoicePreviewFiltersState } from './InvoicePreviewFilters';
import {
  ParsedInvoiceInput,
  convertParsedInvoiceInputToInvoiceInput,
  parseGetInvoicePreviewResponse,
  useGetInvoicePreview,
} from '@/api/services/invoices';
import { RouteProps, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  searchInvoiceDocumentByDocumentId,
  searchInvoiceDocumentByInvoiceId,
} from '@/pages/InvoicePreview/api/services/searchInvoiceDocument';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'react-bootstrap';
import CustomInvoiceTable from './components/CustomInvoiceTable';
import InvoicePreviewHeader from '@/pages/InvoicePreview/InvoicePreviewHeader';
import InvoicePreviewSummary from '@/pages/InvoicePreview/InvoicePreviewSummary';
import InvoicePreviewTable from '@/pages/InvoicePreview/InvoicePreviewTable';
import InvoicePreviewTotals from './InvoicePreviewTotals';
import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import Toast from '@/models/Toast';
import { createComponentState } from '@/state';
import downloadInvoiceDocument from '@/pages/InvoicePreview/api/services/downloadInvoiceDocument';
import equal from 'fast-deep-equal/es6/react';
import generateInvoiceDocument from '@/features/PdfLayout/api/services/generateInvoiceDocument';
import { sendInvoiceEmail } from '@/api/services/invoices/sendInvoiceEmail';
import useConfirmation from '@/hooks/useConfirmation';
import useForm from '@/hooks/useForm';
import useInterval from '@/hooks/useInterval';
import { useRunSaveAndSendInvoice } from '@/api/services/invoices/runSaveAndSendInvoice';
import { useRunSaveAsInvoice } from '@/api/services/invoices/runSaveAsInvoice';
import { useRunSaveInvoice } from '@/api/services/invoices/runSaveInvoice';
import useViewport from '@/hooks/useViewport';

export const initPreview: ParsedInvoiceInput = {
  ...initInvoicePreviewFiltersState,
  // remaining preview information
  due: new Dayts().endOf('day').toString(),
  client: undefined,
  emails: [],
  grandTotal: 0,
  totalSavings: 0,
  grandSubtotal: 0,
  headers: [],
  id: 0,
  invoiced: initInvoicePreviewFiltersState.endDatetime,
  items: [],
  name: '',
  total: 0,
  trips: [],
  totalTripsBilled: 0,
  hasDiscounts: 0,
  customCharges: [],
  chargeType: InvoiceChargeTypeEnum.Trip,
};

export type InvoiceState = { isCustom: boolean };
const initInvoiceState: InvoiceState = { isCustom: false };
export const useInvoice = createComponentState<InvoiceState>(initInvoiceState);

export type PreviewTrip = { id: string; rate?: string; subtotal?: string; count?: number; trip?: Partial<InvoiceTrip> };
export const parseInvoicePreview = async (response: RunPreviewInvoiceResponse): Promise<ParsedInvoiceInput> => {
  const parsedResponse = parseGetInvoicePreviewResponse(response);
  const { output } = parsedResponse;
  const result: ParsedInvoiceInput = Object.keys(initPreview).reduce((acc, key) => {
    let invoice = output?.[key] || output?.invoice?.[key] || initPreview[key];
    if (Array.isArray(invoice)) invoice = Array.from(new Set(invoice));
    acc[key] = invoice;
    return acc;
  }, {} as ParsedInvoiceInput);
  return result;
};
const subtotalUuid = uuid();

const InvoicePreview = (_props: RouteProps): JSX.Element => {
  const [{ isCustom }, setInvoiceState] = useInvoice(({ state, setState }) => [{ isCustom: !!state.isCustom }, setState]);
  const { id: invoiceIdString } = useParams();
  const invoiceId = parseInt(invoiceIdString) || 0;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isCustomFromParams = params.get('custom') === 'true';
  const [
    {
      content: { height: contentHeight },
    },
  ] = useViewport();

  const [preview, onChange, setPreview] = useForm({
    ...initPreview,
    chargeType: !!isCustom || isCustomFromParams ? InvoiceChargeTypeEnum.CustomCharge : InvoiceChargeTypeEnum.Trip,
  });
  const {
    format,
    grandTotal,
    total,
    trips,
    items,
    totalSavings,
    grandSubtotal: _grandSubtotal,
    totalTripsBilled,
    hasDiscounts,
    customCharges,
  } = preview;
  const [processingPDF, setProcessingPDF] = useState(false);
  const [invoiceDocumentId, setInvoiceDocumentId] = useState<string>(null);
  const [processingSend, setProcessingSend] = useState(false);
  const [invoiceDocumentStatus, setInvoiceDocumentStatus] = useState<InvoiceDocumentGenDocumentStatusEnum>(null);

  const [{ loading: saving }, { fetch: runSaveInvoice }] = useRunSaveInvoice();
  const [{ loading: savingAs }, { fetch: runSaveAsInvoice }] = useRunSaveAsInvoice();
  const [{ loading: sending }, { fetch: runSaveAndSendInvoice }] = useRunSaveAndSendInvoice();
  const [{ data, loading: previewLoading }, { fetch: getInvoicePreview }] = useGetInvoicePreview();
  const loading = saving || savingAs || sending || processingSend || previewLoading || processingPDF;
  const { invoiceTerms = '', subtotals = [], summary } = data || {};
  const [output, setOutput] = useState<InvoiceResponse>(data?.output);

  useEffect(() => {
    if (previewLoading || !data?.output) return;
    setOutput(data?.output);
  }, [data, previewLoading]);

  const lastFilters = useRef<InvoicePreviewFiltersState>(null);
  const lastClient = useRef<string>('');
  const lastInvoice = useRef<ParsedInvoiceInput>(null);
  const lastCustomCharges = useRef<InvoiceCustomChargeInput[]>(null);

  const confirmDeleteCustomCharge = useConfirmation();

  const normalizeItems = useCallback(
    (items) => items?.map((item) => ({ ...item, total: typeof item.total === 'string' ? parseFloat(item.total) : item.total })),
    []
  );

  const normalizeTrips = useCallback(
    (trips) =>
      trips?.map(({ createdAt: _createdAt, currency: _currency, id: _id, firstName: _firstName, lastName: _lastName, ...trip }) => trip),
    []
  );

  const normalizeInvoice = useCallback(
    (invoice) => {
      if (!invoice) return null;
      return {
        ...invoice,
        items: normalizeItems(invoice.items),
        trips: normalizeTrips(invoice.trips),
      };
    },
    [normalizeItems, normalizeTrips]
  );
  const hasChanges = useMemo(() => !equal(normalizeInvoice(preview), normalizeInvoice(lastInvoice.current)), [normalizeInvoice, preview]);
  const onSubmit = async (filters?: InvoicePreviewFiltersState): Promise<void> => {
    try {
      filters = filters || lastFilters.current || ({} as InvoicePreviewFiltersState);
      lastFilters.current = filters;
      const query: Partial<ParsedInvoiceInput> = {
        payerProviderId: filters.payerProviderId,
        iataAirlineCodes: filters.iataAirlineCodes,
        airports: filters.airports,
        types: filters.types,
        startDatetime: filters.startDatetime,
        endDatetime: filters.endDatetime,
        format: filters.format,
        tripStatus: filters.tripStatus,
        totalTripsBilled: preview?.totalTripsBilled || trips?.length || 0,
        chargeType: isCustom ? InvoiceChargeTypeEnum.CustomCharge : InvoiceChargeTypeEnum.Trip,
      };
      const payload = convertParsedInvoiceInputToInvoiceInput({
        ...preview,
        ...query,
        customCharges: preview?.customCharges?.map(({ id: _id, ...charge }: InvoiceCustomCharge) => charge) || [],
      });
      if (lastClient.current !== query.payerProviderId) payload.emails = [];
      lastClient.current = payload.payerProviderId;
      const response = await getInvoicePreview(payload);
      if (!response) return;
      parseInvoicePreview(response).then(setPreview);
      lastCustomCharges.current = preview?.customCharges;
    } catch (err) {
      console.error(err);
    }
  };
  const saveAndLoadInvoice = async (method: 'save' | 'saveAs' | 'send') => {
    if (method === 'send' && !hasChanges) {
      const latestDocumentId = output?.invoice?.documents?.sort?.(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )?.[0]?.id;
      await sendInvoiceEmail({
        invoiceId: preview?.id,
        documentId: latestDocumentId,
        toEmails: preview.emails,
      });
      return { parsedInvoice: preview, customChargeSummary: preview?.customCharges };
    } else {
      const fn = method === 'save' ? runSaveInvoice : method === 'saveAs' ? runSaveAsInvoice : runSaveAndSendInvoice;
      const res = await fn(
        convertParsedInvoiceInputToInvoiceInput({
          ...preview,
          totalTripsBilled: preview?.totalTripsBilled || trips?.length || 0,
          customCharges:
            preview?.customCharges?.map(({ id: _id, ...charge }: InvoiceCustomCharge) => ({ ...charge, type: charge.type || null })) || [],
          generatePdf: method === 'send',
        })
      );
      if (res?.invoice?.id) {
        const response = await getInvoicePreview(res?.invoice?.id);
        if (!response) return;
        setOutput(response?.output);
        const parsedInvoice = await parseInvoicePreview(response);
        lastInvoice.current = parsedInvoice;
        lastCustomCharges.current = parsedInvoice?.customCharges;
        setPreview(parsedInvoice);
        return { parsedInvoice, customChargeSummary: response?.customChargeSummary };
      }
    }
  };

  // Calculate where to insert subtotal rows after each group of trips
  // Example: subtotals with counts [3,5,1] → boundaries at indices [2, 7, 8] (0-based)
  const subtotalBoundaries = useMemo(() => {
    const boundaries: { lastTripIndex: number; subtotal: InvoiceRateSubtotal }[] = [];
    let lastIndex = -1;
    (subtotals || []).forEach((subtotal) => {
      const tripCount = subtotal?.count ?? 0;
      if (tripCount > 0) {
        lastIndex += tripCount;
        boundaries.push({ lastTripIndex: lastIndex, subtotal });
      }
    });
    return boundaries;
  }, [subtotals]);

  const [filteredRows, customChargesTotal] = useMemo((): [PreviewTrip[], number] => {
    let groupTripCount = 0;
    let customChargesTotal = 0;

    let currentBoundaryIndex = 0;

    let previewRows: Array<PreviewTrip | InvoiceCustomCharge> = [];
    if (!isCustom) {
      trips.forEach((trip: InvoiceTrip, tripIndex: number) => {
        // Add trip row
        previewRows.push({
          id: trip?.tripId,
          trip: {
            ...trip,
            pilots: trip?.pilots || 0,
            attendants: trip?.attendants || 0,
          },
          rate: formatDollars(trip?.rate || 0),
          count: ++groupTripCount, // 1-based within the current subtotal group
        });

        // If we're at a boundary, insert the subtotal row right after and reset group count
        const boundary = subtotalBoundaries[currentBoundaryIndex];
        if (boundary && tripIndex === boundary.lastTripIndex) {
          previewRows.push({
            id: `${subtotalUuid}-${previewRows.length}`,
            subtotal: formatDollars(boundary.subtotal?.total || 0),
            count: groupTripCount,
          });
          groupTripCount = 0;
          currentBoundaryIndex++;
        }
      });
    } else {
      customCharges.forEach((charge: InvoiceCustomCharge, chargeIndex) => {
        previewRows.push({
          id: charge.id,
          cost: parseFloat(`${charge.cost || 0}`),
          count: chargeIndex + 1,
          type: charge.type,
          note: charge.note,
          index: chargeIndex,
        });
      });
      const [rows, total] = sortCustomCharges(previewRows);
      customChargesTotal = total;
      previewRows = rows;
    }
    return [previewRows, customChargesTotal];
  }, [trips, customCharges, isCustom, subtotalBoundaries]);

  const combineIds = useMemo(() => {
    const ids = [];
    filteredRows.forEach((row) => {
      if (row?.trip?.combineId && !ids.includes(row.trip.combineId)) ids.push(row.trip.combineId);
    });
    return ids;
  }, [filteredRows]);

  const isValidInvoice = true;
  const missingFields = [];

  const onSave = async (): Promise<void> => {
    try {
      if (!isValidInvoice) {
        createNotification('Invoice is missing required information.', Toast.Type.DANGER, 'Invoice');
        return;
      }
      setProcessingPDF(true);
      const { parsedInvoice: saveRes, customChargeSummary: _customChargeSummaryRes } = await saveAndLoadInvoice(
        preview?.id === 0 ? 'saveAs' : 'save'
      );
      if (!saveRes) return;
      const docRes = await generateInvoiceDocument(saveRes.id);
      if (docRes?.genDocumentID) {
        setInvoiceDocumentId(docRes.genDocumentID);
        setInvoiceDocumentStatus(docRes.genDocumentStatus);
      }
      createNotification('Successfully saved invoice', Toast.Type.SUCCESS, 'Invoice');
      navigate(`/invoices/${saveRes?.id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setProcessingPDF(false);
    }
  };
  const onSend = async (): Promise<void> => {
    setProcessingSend(true);
    try {
      if (!isValidInvoice) {
        createNotification('Invoice is missing required information.', Toast.Type.DANGER, 'Invoice');
        return;
      }
      const { parsedInvoice: saveRes, customChargeSummary: _customChargeSummaryRes } = await saveAndLoadInvoice('send');
      if (!saveRes) return;
      const response = await getInvoicePreview(saveRes?.id);
      setOutput((current) => ({ ...current, documents: response?.output?.invoice?.documents || [] }));
      createNotification('Successfully saved invoice and sent invoice email', Toast.Type.SUCCESS, 'Invoice');
      navigate(`/invoices/${saveRes?.id}`);
    } catch (error) {
      console.error(error);
      createNotification('Failed to save invoice or send invoice email', Toast.Type.DANGER, 'Invoice');
    } finally {
      setProcessingSend(false);
    }
  };
  const onSaveAs = async (): Promise<void> => {
    try {
      if (!isValidInvoice) {
        createNotification('Invoice is missing required information.', Toast.Type.DANGER, 'Invoice');
        return;
      }
      setProcessingPDF(true);
      const { parsedInvoice: saveRes, customChargeSummary: _customChargeSummaryRes } = await saveAndLoadInvoice('saveAs');
      if (!saveRes) return;
      const docRes = await generateInvoiceDocument(saveRes.id);
      if (docRes?.genDocumentID) {
        setInvoiceDocumentId(docRes.genDocumentID);
        setInvoiceDocumentStatus(docRes.genDocumentStatus);
      }
      createNotification('Successfully created invoice', Toast.Type.SUCCESS, 'Invoice');
      navigate(`/invoices/${saveRes?.id}`);
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingPDF(false);
    }
  };
  const onDuplicate = async (): Promise<void> => {
    if (!isValidInvoice) {
      createNotification('Invoice is missing required information.', Toast.Type.DANGER, 'Invoice');
      return;
    }
    navigate(`/invoices/0`);
    setPreview((current) => ({ ...current, id: 0 }));
    setOutput((current) => ({ ...current, invoice: { ...current.invoice, id: 0, documents: [] } }));
  };
  const onDownload = async (): Promise<void> => {
    try {
      const documents = await searchInvoiceDocumentByInvoiceId(invoiceId);
      const latestDocumentId = documents?.sort?.((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())?.[0]?.id;
      if (!latestDocumentId) {
        createNotification('No invoice document available for download', Toast.Type.DANGER, 'Invoice');
        return;
      }
      await downloadInvoiceDocument(latestDocumentId);
      createNotification('Invoice downloaded successfully', Toast.Type.SUCCESS, 'Invoice');
    } catch (err) {
      console.warn(err);
      createNotification('Failed to download invoice PDF', Toast.Type.DANGER, 'Invoice PDF');
    }
  };

  const loadExistingInvoice = useCallback(
    async (invoiceId: number): Promise<void> => {
      const response = await getInvoicePreview(invoiceId);
      lastClient.current = response.output.payerProvider.id;
      const parsedInvoice = await parseInvoicePreview(response);
      lastInvoice.current = parsedInvoice;
      lastCustomCharges.current = parsedInvoice?.customCharges;
      setPreview(parsedInvoice);
      const latestDocument = response?.output?.invoice?.documents?.sort?.(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )?.[0];
      if (
        latestDocument &&
        latestDocument.genDocumentStatus !== InvoiceDocumentGenDocumentStatusEnum.Completed &&
        latestDocument.genDocumentStatus !== InvoiceDocumentGenDocumentStatusEnum.Failed
      )
        setInvoiceDocumentId(latestDocument.genDocumentID);
    },
    [getInvoicePreview, setPreview]
  );

  const filters: InvoicePreviewFiltersState = useMemo((): InvoicePreviewFiltersState => {
    if (equal(preview, initPreview)) return;
    const updatedFilters = Object.keys(initInvoicePreviewFiltersState).reduce((acc: InvoicePreviewFiltersState, key: string) => {
      acc[key] = preview?.[key] || initInvoicePreviewFiltersState[key];
      return acc;
    }, {} as InvoicePreviewFiltersState);
    if (lastFilters.current === null) lastFilters.current = updatedFilters;
    return updatedFilters;
  }, [preview]);

  const lastInvoiceId = useRef<number>(null);
  const addLineItem = async (): Promise<void> => {
    setPreview((current) => ({
      ...current,
      customCharges: [...(current.customCharges || []), { id: uuid(), type: '', cost: 0, note: '' }],
    }));
  };

  const onDeleteLineItem =
    (id?: string): (() => Promise<void>) =>
    async (): Promise<void> => {
      confirmDeleteCustomCharge({
        Body: {
          message: id ? 'Delete this custom charge?' : 'Delete all custom charges?',
        },
      })
        .then(() => deleteLineItem(id)())
        .catch(() => console.log('Deletion cancelled'));
    };

  const deleteLineItem =
    (id?: string): (() => void) =>
    (): void => {
      setPreview((current) => ({
        ...current,
        customCharges: id !== undefined ? current.customCharges.filter((charge: InvoiceCustomCharge) => charge.id !== id) : [],
      }));
    };

  useEffect(() => {
    if (previewLoading || !data?.output) return;
    setOutput(data?.output);
  }, [data, previewLoading]);
  useEffect((): void => {
    if (invoiceId === 0 || invoiceId === lastInvoiceId.current) return;
    lastInvoiceId.current = invoiceId;
    loadExistingInvoice(invoiceId);
  }, [invoiceId, loadExistingInvoice]);
  useEffect(() => {
    const { chargeType } = preview || {};
    if ((chargeType === InvoiceChargeTypeEnum.CustomCharge && isCustom) || (chargeType === InvoiceChargeTypeEnum.Trip && !isCustom)) return;
    setInvoiceState((current) => ({
      ...current,
      isCustom: preview?.chargeType === InvoiceChargeTypeEnum.CustomCharge,
    }));
  }, [preview, isCustom, setInvoiceState]);
  useEffect(() => {
    setPreview((current) => ({
      ...current,
      total: customChargesTotal,
      grandTotal: customChargesTotal,
      grandSubtotal: customChargesTotal,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customChargesTotal]);

  useEffect(() => {
    if (isCustomFromParams) {
      setParams((params) => {
        params.delete('custom');
        return params;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useInterval(
    async () => {
      if (!invoiceDocumentId) return;
      const res = await searchInvoiceDocumentByDocumentId(invoiceDocumentId);
      if (
        res?.genDocumentStatus === InvoiceDocumentGenDocumentStatusEnum.Completed ||
        res?.genDocumentStatus === InvoiceDocumentGenDocumentStatusEnum.Failed
      ) {
        setInvoiceDocumentId(null);
        setInvoiceDocumentStatus(res.genDocumentStatus);
        const documents = await searchInvoiceDocumentByInvoiceId(invoiceId);
        setOutput((current) => ({ ...current, invoice: { ...current.invoice, documents } }));
      }
    },
    invoiceDocumentId ? 1000 : null
  );

  if (!contentHeight) return null;

  const pageInfoTotalLabel = isCustom ? 'Items' : 'Total Trips';
  const pageInfoTotal = isCustom ? preview?.customCharges?.length || 0 : totalTripsBilled || trips?.length || 0;

  return (
    <div className="InvoicePreview-Wrapper" style={{ minHeight: contentHeight }}>
      <InvoicePreviewFilters setPreview={setPreview} initValue={filters} onSubmit={onSubmit} loading={loading} invoiceId={invoiceId} />
      <PageInfo>
        {pageInfoTotalLabel}: {pageInfoTotal}
      </PageInfo>
      <LoadingBlur
        loading={loading}
        label={saving || savingAs || sending ? 'Saving...' : processingPDF ? 'Processing PDF...' : 'Loading Invoice...'}
      />
      {!isCustom && filteredRows?.length === 0 && !loading && <NothingToInvoice />}
      {!!isCustom && !preview?.payerProviderId && <NothingToInvoice />}

      {((isCustom && preview?.payerProviderId) || filteredRows?.length > 0) && (
        <>
          <div className="pt-4">
            <InvoicePreviewHeader
              data={{
                ...preview,
                invoiceTerms,
                client: output?.client,
                company: output?.company,
                payerProvider: output?.payerProvider,
                documents: output?.invoice?.documents,
                netBillableTrips: output?.invoice?.netBillableTrips,
              }}
              onChange={onChange}
              onDownload={
                !invoiceDocumentId
                  ? invoiceDocumentStatus === InvoiceDocumentGenDocumentStatusEnum.Completed || invoiceDocumentStatus === null
                    ? onDownload
                    : null
                  : false
              }
              onSave={!invoiceId ? onSaveAs : onSave}
              onDuplicate={onDuplicate}
              onSend={onSend}
              onSaveAs={invoiceId ? onSaveAs : undefined}
              missingFields={missingFields}
              hasChanges={hasChanges}
            />
          </div>
          {!isCustom && (
            <InvoicePreviewTable
              hasDiscounts={hasDiscounts}
              data={filteredRows}
              combineIds={combineIds}
              format={format}
              payerProviderId={output?.payerProvider?.id}
              onRefetch={async () => onSubmit()}
              filteredTypes={preview.types as unknown as InvoiceTripTypeEnum[]}
            />
          )}
          {!!isCustom && preview?.payerProviderId && (
            <>
              <CustomInvoiceTable data={filteredRows} onDelete={onDeleteLineItem} preview={preview} setPreview={setPreview} />
              <div className="CustomPreviewTable-Footer d-flex justify-content-end my-2">
                <Button variant="success" onClick={addLineItem}>
                  Add Line Item
                </Button>
              </div>
            </>
          )}
          <InvoicePreviewTotals
            data={{ total, grandTotal, totalSavings, hasDiscounts }}
            onChange={onChange}
            items={items}
            loading={previewLoading}
          />
          {invoiceId === 0 && !isCustom && <InvoicePreviewSummary summary={summary} />}
        </>
      )}
    </div>
  );
};

const NothingToInvoice = (): JSX.Element => (
  <div className="h-100 d-flex flex-column justify-content-center flex-grow-1">
    <div className="w-100 d-flex justify-content-center">
      <h3 className="text-gray text-center">
        <i className="sv sv-inbox fa-2x"></i>
        <br />
        Nothing to Invoice
      </h3>
    </div>
  </div>
);

const sortCustomCharges = (customCharges) => {
  const sorted = [...customCharges].sort((a, b) => {
    if (!a.type && b.type) return 1;
    if (a.type && !b.type) return -1;
    if ((a.type || '') < (b.type || '')) return -1;
    if ((a.type || '') > (b.type || '')) return 1;
    return (Number(a.cost) || 0) - (Number(b.cost) || 0);
  });

  let groupIndices = {};
  const withGroupIndex = sorted.map((charge) => {
    const type = charge.type || '';
    groupIndices[type] = (groupIndices[type] || 0) + 1;
    return { ...charge, index: groupIndices[type] };
  });

  let customChargesTotal = 0;
  const sortedRows = withGroupIndex.reduce((acc: InvoiceCustomCharge[], curr, idx, arr) => {
    acc.push(curr);
    const isLastOfType = idx === arr.length - 1 || arr[idx + 1].type !== curr.type;
    if (isLastOfType) {
      const groupStart = arr.findIndex((item, i) => i <= idx && item.type === curr.type);
      const group = arr.slice(groupStart, idx + 1);
      const subtotal = group.reduce((sum, c) => sum + parseFloat(c.cost || 0), 0);
      customChargesTotal += subtotal;
      acc.push({
        cost: subtotal,
        type: curr.type,
        note: 'Subtotal',
        id: uuid(),
      } as InvoiceCustomCharge);
    }
    return acc;
  }, []);
  return [sortedRows, customChargesTotal];
};

export default InvoicePreview;
